import React, { createContext, useContext, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { AuthContext } from './AuthContext';
import { authApi } from '../api/endpoints/auth';

const LanguageContext = createContext(null);

const VALID_LOCALES = ['en', 'ar'];

export function LanguageProvider({ children }) {
  const { t, i18n } = useTranslation();
  // Read AuthContext directly (not via useAuth hook) so LanguageProvider
  // remains usable in test fixtures and pre-auth surfaces where the
  // AuthProvider is not mounted. When AuthContext is absent the value
  // is `null` from createContext default — treat that as "no user".
  const auth = useContext(AuthContext);
  const user = auth?.user || null;
  const language = i18n.language || 'ar';
  const isRTL = language === 'ar';

  // Sync document dir/lang on mount and whenever language changes
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRTL]);

  // M-SELF-2 — hydrate from server-persisted user.locale on login.
  // On every successful auth (mount + post-login + post-2FA-verify),
  // AuthContext sets `user`. If the persisted locale differs from the
  // current i18n language, switch — this is what makes the language
  // preference follow the user across browsers / devices instead of
  // living in localStorage on a single machine.
  //
  // Guard: respect an explicit user preference already stored in localStorage
  // (written either by setLanguage() below or by a direct test override).
  // Only sync from the server when there is no stored preference or when the
  // stored preference already matches the server locale (avoids overwriting an
  // explicit choice the user/test made on this device).
  useEffect(() => {
    const serverLocale = user?.locale || user?.orgDefaults?.defaultLanguage;
    // Respect explicit locale overrides stored under either key.
    // 'i18nextLng' is used by e2e tests (and i18next's own LanguageDetector
    // plugin) to set a one-shot language that should outlive the server sync.
    const storedLang =
      localStorage.getItem('i18nextLng') || localStorage.getItem('language');
    if (
      serverLocale &&
      VALID_LOCALES.includes(serverLocale) &&
      serverLocale !== i18n.language &&
      (!storedLang || storedLang === serverLocale)
    ) {
      i18next.changeLanguage(serverLocale);
      localStorage.setItem('language', serverLocale);
    }
    // We intentionally key on user?.locale only; i18n.language changes
    // are driven by setLanguage below and shouldn't re-trigger hydration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.locale]);

  const setLanguage = useCallback((lang) => {
    i18next.changeLanguage(lang);
    localStorage.setItem('language', lang);
    // Also set eagerly for immediate DOM update (useEffect handles mount/sync)
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;

    // M-SELF-2 — persist to backend for any authenticated user. Fire
    // and forget: the localStorage write above already covers the
    // unauthenticated-or-offline case, and a transient PUT failure
    // shouldn't reverse the user's UI choice. We only call updateMe
    // when there's a logged-in user (the route is JWT-gated and would
    // 401 otherwise).
    if (user?.id && VALID_LOCALES.includes(lang) && lang !== user.locale) {
      authApi.updateMe({ locale: lang }).catch(() => {
        // Best-effort persistence — UI already reflects the choice.
        // A real failure surfaces on the next /me hydrate cycle.
      });
    }
  }, [user]);

  // The org default is a fallback for a user who has not set their own
  // timezone, never an override of one who has. Undefined lets Intl fall back
  // to the browser default — the pre-2026-07-12 behaviour.
  const timezone = user?.timezone || user?.orgDefaults?.displayTimezone || undefined;

  const value = useMemo(
    () => ({ language, t, isRTL, setLanguage, timezone }),
    [language, t, isRTL, setLanguage, timezone]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
